import { readRemovedParts } from '../dead-letter-queue/removed-parts'
import { isUnreadable, itemRowsOptions, readableRowsOptions } from '../dead-letter-queue/item-readers'
import type { Tel } from '@w/tel'
import { pageTranslationRowsOptions } from '../translation/page-translation-rows'
import { cardReadColumns, cardVisibleTo, learnerCards } from '../db/question-card-triggers'
import { nativeTongueFor } from '../translation/native-language'
import { queryOptions } from '@w/react-query/db'
import type { SpaceResource } from './spaces-resources'
import type { FlashcardsFeRouterCtx } from './flashcards-fe.router-ctx'
import { pageReadColumns, pageReadSchema } from '../db/page-read'

export function cardsListOptions(resource: SpaceResource) {
  const { db } = resource
  return itemRowsOptions(resource, db.query`SELECT ${cardReadColumns(db.flashcards)} FROM ${learnerCards(resource.userId)}
      WHERE ${cardVisibleTo(db.flashcards, resource.userId)} AND ${db.flashcards.deletedAt.isNull()}
      ORDER BY ${db.flashcards.repeatAt}, ${db.flashcards.fractionalIndex}, ${db.flashcards.qid}`, db.flashcards.__selectSchema, 'card', 'CARD_DATA')
}

export function trainingFlashcardOptions(
  ctx: FlashcardsFeRouterCtx,
  params: { workspaceId: string; flashcardId: string },
) {
  const resource = ctx.spaces.get(params.workspaceId)
  const db = resource.db
  const user = ctx.getUser()
  return readableRowsOptions(resource, db.query`
    SELECT ${cardReadColumns(db.flashcards)} FROM ${learnerCards(user.id)}
    WHERE ${cardVisibleTo(db.flashcards, user.id)}
      AND ${db.flashcards.qid.eq(params.flashcardId)}
  `, db.flashcards.__selectSchema, 'card', 'CARD_DATA')
}

export function trainingPageOptions(
  ctx: FlashcardsFeRouterCtx,
  params: { workspaceId: string; pageId: string; qid?: string },
) {
  const resource = ctx.spaces.get(params.workspaceId)
  const db = resource.db
  const query = db.query`
    SELECT ${pageReadColumns(db.pages)} FROM ${db.pages}
    WHERE ${db.pages.id.eq(params.pageId)}
  `
  const language = nativeTongueFor(ctx.getUser().settings.lexicon?.nativeLanguage, ctx.defaultNativeLanguage)
  const key = (queryKey: unknown[]) => [...queryKey, 'with-translations', language, params.qid ?? null]
  // A refetch of an unchanged row (own-write echo, empty pull) reuses this query's last decoded page.
  const schema = pageReadSchema(params.workspaceId, () => resource.queryClient.getQuery(key(options.queryKey))?.state.data as { data: Uint8Array }[] | undefined, ctx.getUser().id)
  const options = { ...queryOptions(query, schema, { staleTime: Infinity }), queryFn: readableRowsOptions(resource, query, schema, 'page', 'PAGE_DOCUMENT').queryFn }
  const translationChanges = queryOptions(db.query`SELECT ${db.pageTranslations.pageId} FROM ${db.pageTranslations} WHERE ${db.pageTranslations.pageId.eq(params.pageId)} AND ${db.pageTranslations.language.eq(language)}`, {pageId:db.pageTranslations.pageId})
  return { ...options, subscribe: async (invalidate: () => void) => { const releases = await Promise.all([options.subscribe?.(invalidate), translationChanges.subscribe?.(invalidate)]); return () => { for (const release of releases) release?.() } }, queryKey: key(options.queryKey), queryFn: async (qctx?: { tel?: Tel }) => (await Promise.all((await options.queryFn(qctx)).map(async page => {
    const removedParts = params.qid ? await readRemovedParts(resource, page.id, params.qid) : []
    // Undecodable removed parts make this source "Question unavailable", like an undecodable page.
    if (isUnreadable(removedParts)) return []
    return [Object.assign({}, page, { removedParts, translationRows: await pageTranslationRowsOptions(ctx, page, language).queryFn(qctx) })]
  }))).flat() }
}
